/**
 * Filter registry — single source of truth for the SRP faceted-search UI,
 * the URL search-params parser, and the WHERE-clause builder. Adding a new
 * filter is a JSON config change here + (if backed by a new column) a
 * migration.
 *
 * Consumed by:
 *   apps/web/components/public/srp/filter-panel.tsx — widget dispatch
 *   apps/web/lib/inventory/filter-registry.ts        — buildWhere
 *   apps/web/lib/inventory/search-params.ts          — Zod validation
 *   apps/web/app/admin/storefront/inventory/page.tsx — toggle list
 */
export type FilterType =
  | "multiselect-static" // bodyStyle, fuelType, drivetrain, transmission, condition
  | "multiselect-distinct" // make, exteriorColor, interiorColor (DISTINCT from DB)
  | "multiselect-dependent" // model on make, trim on model
  | "multiselect-features" // features array, taxonomy-bound, GIN-indexed
  | "range-int" // year, mileage, cityMpg, highwayMpg
  | "range-currency" // priceCents (input in dollars, stored as cents)
  | "text-exact" // VIN, stockNumber
  | "toggle"; // boolean/value flag — presence in URL means "on"

export type FilterCategory =
  | "Basics"
  | "Performance"
  | "Cosmetic"
  | "Features"
  | "Identity"
  | "History";

interface FilterDefBase {
  key: string;
  label: string;
  category: FilterCategory;
}

export interface MultiselectStaticDef extends FilterDefBase {
  type: "multiselect-static";
  options: readonly string[];
}

export interface MultiselectDistinctDef extends FilterDefBase {
  type: "multiselect-distinct";
}

export interface MultiselectDependentDef extends FilterDefBase {
  type: "multiselect-dependent";
  dependsOn: string;
}

export interface MultiselectFeaturesDef extends FilterDefBase {
  type: "multiselect-features";
}

export interface RangeIntDef extends FilterDefBase {
  type: "range-int";
  rangeMin: number;
  rangeMax: number;
  step?: number;
}

export interface RangeCurrencyDef extends FilterDefBase {
  type: "range-currency";
  rangeMin: number;
  rangeMax: number;
  step: number;
}

export interface TextExactDef extends FilterDefBase {
  type: "text-exact";
}

/** The value applied to the DB column when the toggle is "on". */
export type ToggleValue = boolean | number | string;

export interface ToggleDef extends FilterDefBase {
  type: "toggle";
  /** What to match the column against when the toggle is active. */
  toggleValue: ToggleValue;
}

export type FilterDef =
  | MultiselectStaticDef
  | MultiselectDistinctDef
  | MultiselectDependentDef
  | MultiselectFeaturesDef
  | RangeIntDef
  | RangeCurrencyDef
  | TextExactDef
  | ToggleDef;

export const INVENTORY_FILTERS = [
  { key: "make", label: "Make", type: "multiselect-distinct", category: "Basics" },
  {
    key: "model",
    label: "Model",
    type: "multiselect-dependent",
    category: "Basics",
    dependsOn: "make",
  },
  {
    key: "trim",
    label: "Trim",
    type: "multiselect-dependent",
    category: "Basics",
    dependsOn: "model",
  },
  {
    key: "year",
    label: "Year",
    type: "range-int",
    category: "Basics",
    rangeMin: 1990,
    rangeMax: 2030,
  },
  {
    key: "priceCents",
    label: "Price",
    type: "range-currency",
    category: "Basics",
    rangeMin: 0,
    rangeMax: 200_000_00,
    step: 500_00,
  },
  {
    key: "mileage",
    label: "Mileage",
    type: "range-int",
    category: "Basics",
    rangeMin: 0,
    rangeMax: 300_000,
    step: 5_000,
  },
  {
    key: "bodyStyle",
    label: "Body Style",
    type: "multiselect-static",
    category: "Basics",
    options: [
      "sedan",
      "suv",
      "truck",
      "coupe",
      "convertible",
      "hatchback",
      "wagon",
      "van",
      "minivan",
      "other",
    ],
  },
  {
    key: "condition",
    label: "Condition",
    type: "multiselect-static",
    category: "Basics",
    options: ["new", "used", "certified-pre-owned"],
  },
  {
    key: "fuelType",
    label: "Fuel",
    type: "multiselect-static",
    category: "Performance",
    options: ["gas", "diesel", "electric", "hybrid", "plug-in-hybrid", "other"],
  },
  {
    key: "drivetrain",
    label: "Drivetrain",
    type: "multiselect-static",
    category: "Performance",
    options: ["fwd", "rwd", "awd", "4wd"],
  },
  {
    key: "transmission",
    label: "Transmission",
    type: "multiselect-static",
    category: "Performance",
    options: ["automatic", "manual", "cvt", "other"],
  },
  {
    key: "engineCylinders",
    label: "Cylinders",
    type: "multiselect-static",
    category: "Performance",
    options: ["3", "4", "5", "6", "8", "10", "12"],
  },
  {
    key: "cityMpg",
    label: "City MPG",
    type: "range-int",
    category: "Performance",
    rangeMin: 0,
    rangeMax: 200,
    step: 1,
  },
  {
    key: "highwayMpg",
    label: "Highway MPG",
    type: "range-int",
    category: "Performance",
    rangeMin: 0,
    rangeMax: 200,
    step: 1,
  },
  {
    key: "doors",
    label: "Doors",
    type: "multiselect-static",
    category: "Basics",
    options: ["2", "3", "4", "5"],
  },
  { key: "exteriorColor", label: "Exterior", type: "multiselect-distinct", category: "Cosmetic" },
  { key: "interiorColor", label: "Interior", type: "multiselect-distinct", category: "Cosmetic" },
  { key: "features", label: "Features", type: "multiselect-features", category: "Features" },
  {
    key: "accidentFree",
    label: "No accidents reported",
    type: "toggle",
    category: "History",
    toggleValue: false,
  },
  {
    key: "oneOwner",
    label: "One owner",
    type: "toggle",
    category: "History",
    toggleValue: 1,
  },
  {
    key: "cleanTitleOnly",
    label: "Clean title only",
    type: "toggle",
    category: "History",
    toggleValue: "clean",
  },
  { key: "stockNumber", label: "Stock #", type: "text-exact", category: "Identity" },
  { key: "vin", label: "VIN", type: "text-exact", category: "Identity" },
] as const satisfies readonly FilterDef[];

export type InventoryFilter = (typeof INVENTORY_FILTERS)[number];
export type InventoryFilterKey = InventoryFilter["key"];

export const INVENTORY_FILTER_KEYS: readonly InventoryFilterKey[] = INVENTORY_FILTERS.map(
  (f) => f.key,
);

const FILTERS_BY_KEY: ReadonlyMap<string, FilterDef> = new Map(
  INVENTORY_FILTERS.map((f) => [f.key, f as FilterDef]),
);

export function getFilterDef(key: InventoryFilterKey): FilterDef;
export function getFilterDef(key: string): FilterDef | null;
export function getFilterDef(key: string): FilterDef | null {
  return FILTERS_BY_KEY.get(key) ?? null;
}
